import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
	createUser,
	deleteUser,
	getAllPermisions,
	getAllNotificationTypes,
	getUser,
	getUserProlileClinics,
	getUserProlileLocations,
	getUserProlileSalesReps,
	getUsers,
	updateMsProducts,
	updateUser,
	updateUserProfile,
	updateUserProfileClinics,
	updateUserProfileLocations,
	updateUserProfileSalesReps,
} from '@/services/users';
import type { FilterType } from '@/types/table';
import type {
	CommissionableProduct,
	UpdateUserProfileResponse,
	UpdateUserResponse,
	User,
	UserProfile,
} from '@/types/users';

export const useGetUsers = ({
	page = 1,
	limit = 15,
	enabled = true,
	columnsFilters = '[]',
	search = '',
	status = '',
	globalFilters = '[]',
	sort = '',
}: FilterType) => {
	const role = status as string;
	return useQuery({
		queryKey: [
			'users',
			page,
			limit,
			columnsFilters,
			search,
			status,
			globalFilters,
			sort,
		],
		queryFn: () =>
			getUsers(
				page,
				limit,
				columnsFilters,
				search,
				role,
				globalFilters as string,
				sort as string,
			),
		enabled,
		keepPreviousData: false,
	});
};

export const useGetUser = (type: string, id: string | null, enabled = true) => {
	return useQuery({
		queryKey: ['user', type, id],
		queryFn: () => getUser(type, id as string),
		enabled: enabled && !!id && !!type && typeof id === 'string' && typeof type === 'string',
		keepPreviousData: false,
	});
};

export const useGetAllPermissions = ({
	page = 1,
	limit = 9999,
	enabled = true,
}: {
	page?: number;
	limit?: number;
	enabled?: boolean;
}) => {
	return useQuery({
		queryKey: ['permissions', page, limit],
		queryFn: () => getAllPermisions(page, limit),
		enabled,
		keepPreviousData: false,
	});
};

export const useGetAllNotificationTypes = (enabled = true) => {
	return useQuery({
		queryKey: ['notification-types'],
		queryFn: () => getAllNotificationTypes(),
		enabled,
		keepPreviousData: false,
	});
};

export const useCreateUser = (
	onSuccess?: () => void,
	onError?: (error: unknown) => void,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: createUser,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['users'] });
			onSuccess?.();
		},
		onError: (error: unknown) => {
			onError?.(error);
		},
	});
};

export const useUpdateUser = (
	onSuccess?: () => void,
	onError?: (error: unknown) => void,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: User }) =>
			updateUser(id, data),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ['users'] });
			queryClient.invalidateQueries({ queryKey: ['user', variables.id] });
			onSuccess?.();
		},
		onError: (error: unknown) => {
			onError?.(error);
		},
	});
};

export const useDeleteUser = (
	onSuccess?: () => void,
	onError?: (error: unknown) => void,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => deleteUser(id),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ['users'] });
			queryClient.invalidateQueries({ queryKey: ['user', variables] });
			onSuccess?.();
		},
		onError: (error: unknown) => {
			onError?.(error);
		},
	});
};

export const useUpdateUserProfile = (
	onSuccess?: (data: UpdateUserProfileResponse) => void,
	onError?: (error: unknown) => void,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UserProfile }) =>
			updateUserProfile(id, data),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({
				queryKey: ['profile', variables.id],
			});
			onSuccess?.(data);
		},
		onError: (error: unknown) => {
			onError?.(error);
		},
	});
};

export const useGetUserProfileClinics = (userId: string) => {
	return useQuery({
		queryKey: ['user-profile-clinics', userId],
		queryFn: () => getUserProlileClinics(userId),
		enabled: !!userId,
		keepPreviousData: false,
	});
};

export const useGetUserProfileLocations = (userId: string) => {
	return useQuery({
		queryKey: ['user-profile-locations', userId],
		queryFn: () => getUserProlileLocations(userId),
		enabled: !!userId,
		keepPreviousData: false,
	});
};

export const useGetUserLocations = (userId: string, port: string) => {
	return useQuery({
		queryKey: ['user-profile-locations', userId],
		queryFn: () => getUserProlileLocations(userId),
		enabled: !!userId && port === 'SalesRep',
		keepPreviousData: false,
	});
};

export const useGetUserProfileSalesReps = (userId: string) => {
	return useQuery({
		queryKey: ['user-profile-salesreps', userId],
		queryFn: () => getUserProlileSalesReps(userId),
		enabled: !!userId,
		keepPreviousData: false,
	});
};

export const useGetUserSalesReps = (userId: string, port: string) => {
	return useQuery({
		queryKey: ['user-salesreps', userId],
		queryFn: () => getUserProlileSalesReps(userId),
		enabled: !!userId && port === 'SalesRep',
		keepPreviousData: false,
	});
};

export const useUpdateUserProfileClinics = (
	userId: string,
	onSuccess?: (data: UpdateUserResponse) => void,
	onError?: (error: unknown) => void,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: string[]) => updateUserProfileClinics(userId, data),
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: ['user-profile-clinics', userId],
			});
			onSuccess?.(data);
		},
		onError: (error: unknown) => {
			onError?.(error);
		},
	});
};

export const useUpdateUserProfileLocations = (
	userId: string,
	onSuccess?: (data: UpdateUserResponse) => void,
	onError?: (error: unknown) => void,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: string[]) => updateUserProfileLocations(userId, data),
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: ['user-profile-locations', userId],
			});
			onSuccess?.(data);
		},
		onError: (error: unknown) => {
			onError?.(error);
		},
	});
};

export const useUpdateUserProfileSalesReps = (
	userId: string,
	onSuccess?: (data: UpdateUserResponse) => void,
	onError?: (error: unknown) => void,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: string[]) => updateUserProfileSalesReps(userId, data),
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: ['user-profile-salesreps', userId],
			});
			onSuccess?.(data);
		},
		onError: (error: unknown) => {
			onError?.(error);
		},
	});
};

export const useUpdateUserMsProducts = (userId: string) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: CommissionableProduct[]) => updateMsProducts(userId, data),
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: ['user-ms-products', userId],
			});
			toast.success('MS Products updated successfully.');
		},
		onError: (error: unknown) => {
			const apiError = error as {
				response?: { data?: { message?: string } };
			};
			toast.error(
				apiError?.response?.data?.message ||
					'Failed to update MS Products. Please try again.',
			);
		},
	});
};
